import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  CheckSquare,
  LayoutGrid,
  ArrowRight,
  CircleCheckBig,
  BookOpen,
  HelpCircle,
  Shield,
  Loader2,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useGetModulesQuery, useGetExamResultQuery, useGetProgressQuery } from "../redux/Clinictrainingapi";

const COURSE_TITLE = "NexaLife HIPAA Compliance Certification Training";
const COURSE_DESCRIPTION =
  "This course supports HIPAA workforce education and ensures all clinical staff maintain high standards.";
const COURSE_DURATION = "2 hours";
const PASSING_SCORE = 80;

const LEARNING_OBJECTIVES = [
  "Understand what HIPAA requires",
  "Protect patient information",
  "Avoid common violations",
  "Use NexaLife properly",
  "Respond correctly to incidents",
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: modules, isLoading, error } = useGetModulesQuery();

  // Always scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const { data: certList } = useGetExamResultQuery();
  const hasCertificate = Array.isArray(certList) ? certList.length > 0 : !!certList;


  // Fetch progress directly — no context timing issues
  const { data: progressData, isLoading: progressLoading } = useGetProgressQuery();

  const totalModules = modules?.length || 0;
  const dataReady = !!modules && !isLoading && !progressLoading;

  // Determine resume label directly from server data
  const resumeLabel = (() => {
    if (!dataReady) return "Start Course";
    if (!Array.isArray(progressData) || progressData.length === 0) return "Start Course";
    return "Resume Course";
  })();

  const handleStartCourse = () => {
    if (!dataReady) return;
    const sortedModules = [...modules].sort((a, b) => a.order - b.order);

    // No server progress → start from the beginning
    if (!Array.isArray(progressData) || progressData.length === 0) {
      navigate(`/module/${sortedModules[0].order}`);
      return;
    }

    // Find the latest incomplete module from server progress
    // Sort progress entries by updated_at to find most recent
    const sortedProgress = [...progressData].sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );

    // Find the first module that isn't completed
    const activeEntry = sortedProgress.find((p) => !p.is_completed) ?? sortedProgress[0];

    // Match progress entry to module data
    const resumeModule = sortedModules.find((m) => m.id === activeEntry.module);
    if (!resumeModule) {
      navigate(`/module/${sortedModules[0].order}`);
      return;
    }

    const sections = [...(resumeModule.sections ?? [])].sort(
      (a, b) => a.order - b.order
    );

    // If module is completed, find the next incomplete module
    if (activeEntry.is_completed) {
      const nextModule = sortedModules.find((m) => {
        const prog = progressData.find((p) => p.module === m.id);
        return !prog || !prog.is_completed;
      });
      if (nextModule) {
        navigate(`/module/${nextModule.order}`);
      } else {
        // All modules complete — go to last module's completed state
        navigate(`/module/${resumeModule.order}?completed=true`);
      }
      return;
    }

    // Module in progress — find current_section and navigate to next
    const currentSectionObj = sections.find((s) => s.id === activeEntry.current_section);

    if (!currentSectionObj) {
      // current_section not found → go to module intro
      navigate(`/module/${resumeModule.order}`);
      return;
    }

    // Find next section after current
    const nextSection = sections.find((s) => s.order > currentSectionObj.order);

    if (nextSection) {
      navigate(`/module/${resumeModule.order}/lesson/${nextSection.order}`);
    } else {
      // All sections done — check for quiz
      const hasQuiz = resumeModule.knowledge_checks?.length > 0;
      if (hasQuiz) {
        navigate(`/module/${resumeModule.order}/quiz`);
      } else {
        navigate(`/module/${resumeModule.order}?completed=true`);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">
          Failed to load course data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      {/* ml-0 on mobile, ml-16 on md+ for sidebar */}
      <div className="ml-0 md:ml-16 min-h-screen flex flex-col">

        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="px-4 sm:px-8 pt-6 sm:pt-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Stack on mobile, row on sm+ */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-col">
                {/* Breadcrumb — hide the separator on tiny screens */}
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase">
                    Education Portal
                  </span>
                  <span className="text-gray-300 hidden sm:inline">———</span>
                  <span className="text-xs font-bold text-primary tracking-widest uppercase">
                    Mandatory Training
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Course Overview
                </h1>
              </div>

              {/* Badge — full width on mobile, auto on sm+ */}
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 px-3 sm:px-4 py-1.5 rounded-full self-start sm:self-auto">
                <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                <span className="text-[10px] sm:text-xs font-bold tracking-wide uppercase leading-tight">
                  Required Before Provider Activation
                </span>
              </div>
            </div>
          </motion.div>
        </header>

        {/* ── Main card ──────────────────────────────────────────── */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 flex-1">
          <motion.div
            className="bg-white rounded-2xl sm:rounded-3xl border border-[#F1F5F9] overflow-hidden"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {/* Stack on mobile, row on lg+ */}
            <div className="flex flex-col lg:flex-row">

              {/* ── Left panel ───────────────────────────────────── */}
              <div className="flex-1 bg-gray-50/70 p-6 sm:p-10 lg:p-12">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-3 sm:mb-4">
                    {COURSE_TITLE}
                  </h2>
                  <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 max-w-md text-sm sm:text-base">
                    {COURSE_DESCRIPTION}
                  </p>

                  <h3 className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4 sm:mb-5">
                    Core Learning Objectives
                  </h3>

                  {/* Single col on mobile, 2 cols on sm+ */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 sm:gap-y-4">
                    {LEARNING_OBJECTIVES.map((obj, i) => (
                      <motion.div
                        key={i}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 + i * 0.06 }}
                      >
                        <CircleCheckBig
                          size={20}
                          className="bg-[#ECFEFF] p-1 text-primary rounded-xl shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-gray-800 font-medium leading-snug">
                          {obj}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* ── Right panel ──────────────────────────────────── */}
              {/* Full width on mobile, fixed width on lg+ */}
              <div className="w-full lg:w-80 xl:w-96 bg-[#F8FAFC80] p-6 sm:p-8 lg:p-10 flex flex-col border-t lg:border-t-0 lg:border-l border-[#F1F5F9]">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="flex flex-col h-full"
                >
                  <h3 className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4 sm:mb-5 text-center">
                    Course Details
                  </h3>

                  {/* Detail cards — row on mobile (compact), stacked on lg */}
                  <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 sm:gap-3 mb-5 sm:mb-6">
                    {[
                      { icon: Clock,       label: "DURATION",     value: COURSE_DURATION },
                      { icon: CheckSquare, label: "PASSING SCORE", value: `${PASSING_SCORE}%` },
                      { icon: LayoutGrid,  label: "MODULES",      value: `${totalModules} Units` },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="bg-white border border-[#F1F5F9] rounded-xl p-3 sm:p-4 flex flex-col lg:flex-row items-center lg:items-center gap-2 lg:gap-4"
                      >
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F8FAFC] flex items-center justify-center shrink-0">
                          <item.icon size={16} className="text-gray-400 sm:hidden" />
                          <item.icon size={18} className="text-gray-400 hidden sm:block" />
                        </div>
                        <div className="text-center lg:text-left">
                          <div className="text-[9px] sm:text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                            {item.label}
                          </div>
                          <div className="text-xs sm:text-sm font-semibold text-gray-800 leading-tight">
                            {item.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA button */}
                  {hasCertificate ? (
                    <button
                      onClick={() => navigate("/final-exam/results")}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-3.5 sm:py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-3 transition-all cursor-pointer shadow-lg shadow-green-500/20"
                    >
                      View Certificate
                      <ArrowRight size={18} />
                    </button>
                  ) : (
                    <button
                      onClick={handleStartCourse}
                      disabled={!dataReady}
                      className="w-full bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-dark hover:to-primary text-white py-3.5 sm:py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-3 transition-all cursor-pointer shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resumeLabel}
                      <ArrowRight size={18} />
                    </button>
                  )}

                  <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4">
                    <Shield size={14} className="text-gray-400" />
                    <span className="text-[10px] font-semibold text-gray-400 tracking-widest uppercase">
                      NexaLife HIPAA Training
                    </span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <footer className="px-4 sm:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center gap-4 sm:gap-6">
            <button className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-primary transition-colors cursor-pointer uppercase tracking-wider">
              <BookOpen size={14} /> Guidance Notes
            </button>
            <button className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-primary transition-colors cursor-pointer uppercase tracking-wider">
              <HelpCircle size={14} /> LMS Support
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400 tracking-wider uppercase">
            <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
            Revision 2024.Q4
          </div>
        </footer>
      </div>
    </div>
  );
}